import React, { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import CustomModal from 'components/CustomModal/CustomModal'
import { DEFAULT_TEAM_LIMIT } from 'constants/plans'
import Add from './components/Add'

export const STAGE_EDIT = 0
export const STAGE_SUCCESS = 1
export const STAGE_FAILED = 2
export const STAGE_SUCCESS_MANUAL = 3

const REJECTION_REASON_MESSAGES = {
  TEAM_MEMBER_SELF_ADD: 'you cannot add yourself',
  TEAM_MEMBER_ALREADY_PAID: 'already has its own paid account',
  TEAM_MEMBER_ALREADY_IN_TEAM: 'already belongs to another team'
}

const FAILURE_MESSAGES = {
  ACCOUNT_NOT_EXISTS: 'Your account could not be found. Please refresh and try again.'
}

const generateSummaryMessage = (addedCount, removedCount, isManual = false, hasRejections = false) => {
  if (isManual) {
    return "We've received your request and will update your account shortly"
  }
  if (addedCount > 0 && removedCount > 0) {
    return `Email invite${addedCount > 1 ? 's have' : ' has'} been sent to ${addedCount} new member${addedCount > 1 ? 's' : ''} and ${removedCount} member${removedCount > 1 ? 's have' : ' has'} been removed`
  } else if (addedCount > 0) {
    return `Email invite${addedCount > 1 ? 's have' : ' has'} been sent to ${addedCount} new member${addedCount > 1 ? 's' : ''}`
  } else if (removedCount > 0) {
    return `${removedCount} team member${removedCount > 1 ? 's have' : ' has'} been removed successfully`
  } else if (hasRejections) {
    return 'No invitations were sent. Review the addresses below.'
  }
  return 'Team members updated successfully'
}

const Success = ({ onHide, result }) => {
  const { addedCount = 0, invitedEmails = [], removedCount = 0, isManual = false, rejected = [] } = result || {}
  const nothingSucceeded = addedCount === 0 && removedCount === 0 && !isManual
  const summary = generateSummaryMessage(addedCount, removedCount, isManual, rejected.length > 0)
  const heading = isManual
    ? 'Request received'
    : nothingSucceeded && rejected.length > 0
      ? 'No members added'
      : addedCount > 0 && removedCount === 0
        ? 'Invitations sent'
        : 'Team updated'

  return (
    <div className="p-4">
      <div className="text-center mb-3">
        <img
          className="mb-2"
          height={22}
          width={22}
          src={nothingSucceeded && rejected.length > 0 ? '/assets/icons/warning-yellow.svg' : '/assets/icons/tick.png'}
          alt=""
        />
        <h4 className="color-purple mb-1">{heading}</h4>
        <p className="grey small mb-0">{summary}</p>
      </div>
      {!isManual && invitedEmails.length > 0 && (
        <div className="mb-3">
          <div className="small text-muted mb-2">Invited:</div>
          <ul className="list-unstyled mb-0">
            {invitedEmails.map((email) => (
              <li key={email} className="small py-1 text-break">
                {email}
              </li>
            ))}
          </ul>
        </div>
      )}
      {rejected.length > 0 && (
        <div className="mb-3">
          <div className="small text-muted mb-2">Could not add:</div>
          <ul className="list-unstyled mb-0">
            {rejected.map(({ email, reason }) => (
              <li key={email} className="d-flex align-items-start mb-2 text-break">
                <img className="me-2 mt-1" src="/assets/icons/x.png" alt="" width="12" height="12" />
                <span>
                  {email}{' '}
                  <span className="text-muted small">
                    ({REJECTION_REASON_MESSAGES[reason] || 'could not be added'})
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Button className="w-100" type="button" variant="primary" onClick={onHide}>
        Done
      </Button>
    </div>
  )
}

const Failed = ({ onHide, msg }) => (
  <div className="p-4">
    <div className="text-center mb-4">
      <h4 className="text-danger mb-0 d-flex align-items-center justify-content-center">
        <img className="me-2" height={22} width={22} src="/assets/icons/x.png" alt="" />
        {msg || 'Failed to update team members. Please try again.'}
      </h4>
    </div>
    <div className="d-flex justify-content-end">
      <Button type="submit" variant="primary" onClick={onHide}>
        Close
      </Button>
    </div>
  </div>
)

const AddTeamMembersModal = ({ show, currentTeamMembers, onHide, userProfile }) => {
  const [stage, setStage] = useState(STAGE_EDIT)
  const [operationResult, setOperationResult] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const history = useHistory()

  useEffect(() => {
    setStage(STAGE_EDIT)
    setOperationResult(null)
    setErrorMessage('')
  }, [show])

  useEffect(() => {
    if (show) {
      history.push({ pathname: '/manage/team' })
    }
  }, [show, history])

  const handleOperationComplete = (result) => {
    setOperationResult(result)
    setStage(result.isManual ? STAGE_SUCCESS_MANUAL : STAGE_SUCCESS)
  }

  const handleOperationFailed = (code) => {
    setErrorMessage(FAILURE_MESSAGES[code] || '')
    setStage(STAGE_FAILED)
  }

  const onClose = () => {
    history.push({ pathname: '/manage' })
    onHide()
  }

  return (
    <CustomModal
      aria-labelledby="contained-modal-title-vcenter"
      show={show}
      onHide={onClose}
      enforceFocus={false}
      centered
    >
      {stage === STAGE_EDIT && (
        <Add
          existingTeamMembers={currentTeamMembers}
          onHide={onHide}
          onOperationComplete={handleOperationComplete}
          onOperationFailed={handleOperationFailed}
          teamMemberLimit={Math.max(DEFAULT_TEAM_LIMIT, userProfile?.teamSize || 0, currentTeamMembers.length)}
          setStage={setStage}
          userProfile={userProfile}
        />
      )}
      {(stage === STAGE_SUCCESS || stage === STAGE_SUCCESS_MANUAL) && (
        <Success onHide={onHide} result={operationResult} />
      )}
      {stage === STAGE_FAILED && <Failed onHide={onHide} msg={errorMessage} />}
    </CustomModal>
  )
}

export default AddTeamMembersModal
